import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { usePostHog } from 'posthog-js/react';

const DEFAULT_ENDPOINT = 'http://localhost:11434/api/chat';
const DEFAULT_MODEL = 'glm-4.7-flash:latest';
const LEGACY_DEFAULT_MODEL = 'llama3.2';
const MODEL_MIGRATION_KEY = 'ollama-model-migrated-v2';
const MODEL_OPTIONS = [
  'glm-4.7-flash:latest',
  'qwen3.5:latest',
  'llama3.2',
  'qwen3:1.7b',
];
const MAX_HISTORY_MESSAGES = 4;
const SITE_SYSTEM_PROMPT = `You are the website assistant for Xander Jennie's personal site. Answer only questions about Xander, his work, his projects, his experience, his background, and the content visible on this website. Use the supplied website context as the source of truth. If a question is unrelated to the website or asks for general-purpose help, politely refuse and redirect the user to ask about Xander or the site. Keep answers concise, clear, and grounded in the provided context. If the website context does not contain the answer, say that the site does not provide that information.`;
const SITE_KNOWLEDGE_BASE = `Website context:
- Name: Xander Jennie.
- Role: Product Manager and hobby developer.
- Current work: Product Manager at Just Eat Takeaway.com, currently in Grocery & Retail.
- Homepage summary: Xander is a product manager who likes to tinker with code.
- About: Experienced product manager working on B2B and B2C digital products, creates web applications and browser extensions to solve practical problems, and writes articles about interesting concepts.
- Languages: Native English and Dutch, conversational Spanish, native-level Papiamentu.
- Background: From Curaçao.
- Education: Bachelor of Science in Information & Communication Technology (ICT), graduated cum laude.
- Interests: Traveling, exploring new technologies, coding side projects.
- Experience:
  - Product Manager, Just Eat Takeaway - Grocery & Retail, Amsterdam, Netherlands, Jul 2024 to Present.
  - Product Manager, Just Eat Takeaway - Partner Catalogue, Amsterdam, Netherlands, Feb 2023 to Jun 2024.
  - Associate Product Manager, Just Eat Takeaway - Partner Access, Amsterdam, Netherlands, Feb 2022 to Jan 2023.
- Projects:
  - Power Tools for Google Tasks: browser extension for Google Tasks with duration support and more.
  - Authentication Hell: intentionally frustrating and unnecessarily complex authentication flow.
  - Pokedex: feature-rich Pokedex app with browsing, battle simulation, AI battle advice, and gym preparation.
  - Swiper: work in progress focused on simplifying what to cook tonight.
- Contact: The website includes a contact form for reaching out to Xander.
- Scope rule: Only answer questions about the website, Xander's background, experience, projects, and contact options.`;
const IN_SCOPE_KEYWORDS = [
  'xander',
  'jennie',
  'website',
  'site',
  'portfolio',
  'about',
  'project',
  'projects',
  'experience',
  'work',
  'career',
  'contact',
  'email',
  'background',
  'education',
  'language',
  'languages',
  'dutch',
  'english',
  'spanish',
  'papiamentu',
  'curaçao',
  'curacao',
  'amsterdam',
  'just eat',
  'takeaway',
  'product manager',
  'duration for google tasks',
  'authentication hell',
  'pokedex',
  'swiper',
  'browser extension',
];
const IN_SCOPE_PHRASES = [
  'who are you',
  'what do you do',
  'where do you work',
  'what do you work on',
  'what projects',
  'how can i contact',
  'how do i contact',
  'what languages do you speak',
  'where are you based',
  'tell me about xander',
];
const OUT_OF_SCOPE_MESSAGE = `I can help with questions about Xander, his experience, his projects, and the content on this website. I can't help with unrelated general questions.`;

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background:
    radial-gradient(circle at top right, ${props => props.themeMode === 'light' ? 'rgba(255, 140, 0, 0.12)' : 'rgba(163, 147, 245, 0.18)'}, transparent 32%),
    ${props => props.themeMode === 'light' ? '#fcfbf7' : '#17171b'};
  color: ${props => props.themeMode === 'light' ? '#1f1f1f' : '#f2f2f4'};
`;

const Intro = styled.div`
  padding: 22px 24px 18px;
  border-bottom: 1px solid ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
  display: grid;
  gap: 14px;
`;

const IntroTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${props => props.themeMode === 'light' ? '#8c5a1f' : '#d7c8ff'};
  font-weight: 700;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.themeMode === 'light' ? '#ff8c00' : '#a393f5'};
  box-shadow: 0 0 0 6px ${props => props.themeMode === 'light' ? 'rgba(255, 140, 0, 0.12)' : 'rgba(163, 147, 245, 0.16)'};
`;

const Heading = styled.h2`
  font-size: clamp(1.6rem, 2vw, 2rem);
  line-height: 1.1;
  margin: 0 0 8px;
  font-family: 'Georgia', 'Times New Roman', serif;
`;

const Description = styled.p`
  margin: 0;
  max-width: 58ch;
  line-height: 1.6;
  color: ${props => props.themeMode === 'light' ? '#5d5d66' : '#b8b8c2'};
`;

const ConfigRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: ${props => props.themeMode === 'light' ? '#6f6f79' : '#9f9fac'};
`;

const Input = styled.input`
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.12)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.themeMode === 'light' ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.04)'};
  color: inherit;
  padding: 12px 14px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: var(--accent-color);
    box-shadow: 0 0 0 3px ${props => props.themeMode === 'light' ? 'rgba(255, 140, 0, 0.12)' : 'rgba(163, 147, 245, 0.16)'};
  }
`;

const Select = styled.select`
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.12)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.themeMode === 'light' ? 'rgba(255,255,255,0.72)' : 'rgba(255,255,255,0.04)'};
  color: inherit;
  padding: 12px 14px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: var(--accent-color);
    box-shadow: 0 0 0 3px ${props => props.themeMode === 'light' ? 'rgba(255, 140, 0, 0.12)' : 'rgba(163, 147, 245, 0.16)'};
  }
`;

const MessageArea = styled.div`
  flex: 1;
  padding: 22px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow-y: auto;
`;

const EmptyState = styled.div`
  margin: auto 0;
  border: 1px dashed ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.12)' : 'rgba(255, 255, 255, 0.12)'};
  border-radius: 18px;
  padding: 20px;
  background: ${props => props.themeMode === 'light' ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.03)'};
`;

const EmptyTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 1rem;
`;

const EmptyText = styled.p`
  margin: 0;
  line-height: 1.6;
  color: ${props => props.themeMode === 'light' ? '#60606b' : '#b4b4bf'};
`;

const Bubble = styled.div`
  align-self: ${props => props.role === 'user' ? 'flex-end' : 'flex-start'};
  max-width: min(78%, 62ch);
  padding: 14px 16px;
  border-radius: 18px;
  white-space: pre-wrap;
  line-height: 1.6;
  background: ${props => {
    if (props.role === 'user') {
      return props.themeMode === 'light'
        ? 'linear-gradient(135deg, rgba(255, 140, 0, 0.18), rgba(255, 140, 0, 0.08))'
        : 'linear-gradient(135deg, rgba(163, 147, 245, 0.28), rgba(163, 147, 245, 0.12))';
    }
    return props.themeMode === 'light' ? 'rgba(255,255,255,0.82)' : 'rgba(255,255,255,0.05)';
  }};
  border: 1px solid ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
  box-shadow: ${props => props.role === 'user' ? 'none' : '0 10px 30px rgba(0,0,0,0.06)'};
`;

const BubbleLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
  opacity: 0.72;
`;

const Composer = styled.form`
  border-top: 1px solid ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.08)' : 'rgba(255, 255, 255, 0.08)'};
  padding: 16px 24px 22px;
  display: grid;
  gap: 12px;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  resize: vertical;
  border-radius: 18px;
  border: 1px solid ${props => props.themeMode === 'light' ? 'rgba(31, 31, 31, 0.12)' : 'rgba(255, 255, 255, 0.1)'};
  background: ${props => props.themeMode === 'light' ? 'rgba(255,255,255,0.76)' : 'rgba(255,255,255,0.04)'};
  color: inherit;
  padding: 16px;
  font: inherit;
  line-height: 1.5;
  outline: none;

  &:focus {
    border-color: var(--accent-color);
    box-shadow: 0 0 0 3px ${props => props.themeMode === 'light' ? 'rgba(255, 140, 0, 0.12)' : 'rgba(163, 147, 245, 0.16)'};
  }
`;

const ComposerMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${props => props.themeMode === 'light' ? '#6a6a74' : '#a6a6b2'};
`;

const SendButton = styled.button`
  border: 0;
  border-radius: 999px;
  padding: 12px 18px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${props => props.disabled
    ? (props.themeMode === 'light' ? '#d7d7dc' : '#3c3c46')
    : 'var(--accent-color)'};
  color: ${props => props.disabled
    ? (props.themeMode === 'light' ? '#7b7b84' : '#9f9fa8')
    : '#111'};
`;

const ErrorBanner = styled.div`
  border-radius: 14px;
  padding: 12px 14px;
  background: ${props => props.themeMode === 'light' ? 'rgba(214, 76, 76, 0.09)' : 'rgba(255, 107, 107, 0.12)'};
  border: 1px solid ${props => props.themeMode === 'light' ? 'rgba(214, 76, 76, 0.18)' : 'rgba(255, 107, 107, 0.24)'};
  color: ${props => props.themeMode === 'light' ? '#8f2b2b' : '#ffb0b0'};
  line-height: 1.5;
`;

const Typing = styled.div`
  align-self: flex-start;
  font-size: 13px;
  color: ${props => props.themeMode === 'light' ? '#6b6b76' : '#a9a9b4'};
`;

const createRequestMessages = (messages, nextPrompt) => {
  const history = messages.slice(-MAX_HISTORY_MESSAGES).map(message => ({
    role: message.role,
    content: message.content,
  }));

  return [
    { role: 'system', content: SITE_SYSTEM_PROMPT },
    { role: 'system', content: SITE_KNOWLEDGE_BASE },
    ...history,
    { role: 'user', content: nextPrompt },
  ];
};

const isInScopeQuestion = (prompt) => {
  const normalizedPrompt = prompt.trim().toLowerCase();

  if (!normalizedPrompt) {
    return false;
  }

  return IN_SCOPE_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword))
    || IN_SCOPE_PHRASES.some((phrase) => normalizedPrompt.includes(phrase));
};

const ChatWindow = ({ theme }) => {
  const posthog = usePostHog();
  const [endpoint, setEndpoint] = useState(() => localStorage.getItem('ollama-endpoint') || DEFAULT_ENDPOINT);
  const [model, setModel] = useState(() => {
    const savedModel = localStorage.getItem('ollama-model');
    const hasMigratedLegacyDefault = localStorage.getItem(MODEL_MIGRATION_KEY) === 'true';

    if (!hasMigratedLegacyDefault && savedModel === LEGACY_DEFAULT_MODEL) {
      localStorage.setItem(MODEL_MIGRATION_KEY, 'true');
      localStorage.setItem('ollama-model', DEFAULT_MODEL);
      return DEFAULT_MODEL;
    }

    return savedModel || DEFAULT_MODEL;
  });
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('ollama-endpoint', endpoint);
  }, [endpoint]);

  useEffect(() => {
    localStorage.setItem('ollama-model', model);
  }, [model]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending]);

  const canSend = useMemo(() => draft.trim().length > 0 && model.trim().length > 0 && endpoint.trim().length > 0 && !isSending, [draft, model, endpoint, isSending]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const prompt = draft.trim();

    if (!prompt || isSending) {
      return;
    }

    setDraft('');
    setError('');
    setIsSending(true);

    const userMessage = { role: 'user', content: prompt };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);

    posthog?.capture('chat_message_sent', { model, prompt_length: prompt.length });

    if (!isInScopeQuestion(prompt)) {
      posthog?.capture('chat_out_of_scope', { prompt_length: prompt.length });
      setMessages([
        ...nextMessages,
        { role: 'assistant', content: OUT_OF_SCOPE_MESSAGE },
      ]);
      setIsSending(false);
      return;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model,
          messages: createRequestMessages(messages, prompt),
          stream: false,
        }),
      });

      if (!response.ok) {
        throw new Error(`Ollama returned ${response.status}. Make sure the model exists and the app can reach ${endpoint}.`);
      }

      const payload = await response.json();
      const assistantMessage = payload?.message?.content?.trim();

      if (!assistantMessage) {
        throw new Error('Ollama responded, but no message content was returned.');
      }

      posthog?.capture('chat_response_received', { model, response_length: assistantMessage.length });
      setMessages([...nextMessages, { role: 'assistant', content: assistantMessage }]);
    } catch (requestError) {
      const errorMessage = requestError instanceof Error
        ? requestError.message
        : 'The request failed before Ollama could answer.';
      posthog?.capture('chat_request_failed', { model, error: errorMessage });
      setError(errorMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <ChatShell themeMode={theme}>
      <Intro themeMode={theme}>
        <IntroTop>
          <div>
            <Eyebrow themeMode={theme}>
              <StatusDot themeMode={theme} />
              Local LLM
            </Eyebrow>
            <Heading>Chat with an LLM right from here.</Heading>
            <Description themeMode={theme}>
              This window talks directly to your local Ollama HTTP API, so you can keep the interaction inside the homepage instead of opening a separate tool.
            </Description>
            <Description themeMode={theme} style={{ marginTop: '8px' }}>
              It is scoped to questions about Xander, his background, his projects, and the content on this site.
            </Description>
          </div>
        </IntroTop>

        <ConfigRow>
          <Field themeMode={theme}>
            Model
            <Select
              themeMode={theme}
              value={model}
              onChange={(event) => setModel(event.target.value)}
            >
              {MODEL_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Field>
          <Field themeMode={theme}>
            Endpoint
            <Input
              themeMode={theme}
              value={endpoint}
              onChange={(event) => setEndpoint(event.target.value)}
              placeholder={DEFAULT_ENDPOINT}
            />
          </Field>
        </ConfigRow>

        {error && <ErrorBanner themeMode={theme}>{error}</ErrorBanner>}
      </Intro>

      <MessageArea>
        {messages.length === 0 ? (
          <EmptyState themeMode={theme}>
            <EmptyTitle>Ready when you are.</EmptyTitle>
            <EmptyText themeMode={theme}>
              Try a personal prompt like “Summarize what kind of builder Xander is from this site” or a practical one like “Help me draft a product launch checklist.”
            </EmptyText>
          </EmptyState>
        ) : (
          messages.map((message, index) => (
            <Bubble key={`${message.role}-${index}`} role={message.role} themeMode={theme}>
              <BubbleLabel>{message.role === 'user' ? 'You' : 'Ollama'}</BubbleLabel>
              {message.content}
            </Bubble>
          ))
        )}

        {isSending && <Typing themeMode={theme}>Ollama is thinking...</Typing>}
        <div ref={bottomRef} />
      </MessageArea>

      <Composer onSubmit={handleSubmit} themeMode={theme}>
        <Textarea
          themeMode={theme}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              if (canSend) {
                handleSubmit(event);
              }
            }
          }}
          placeholder="Ask something..."
        />
        <ComposerMeta>
          <Hint themeMode={theme}>Press Enter to send, Shift+Enter for a new line.</Hint>
          <SendButton type="submit" disabled={!canSend} themeMode={theme}>
            {isSending ? 'Sending...' : 'Send'}
          </SendButton>
        </ComposerMeta>
      </Composer>
    </ChatShell>
  );
};

export default ChatWindow;
